// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/donation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { donationsApi } from "../../api/donations";
import type { Donation } from "../../api/types";
// override-block:imports BEGIN
import type { ReactNode } from "react";
import type { DonationStatus } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1330 — bulk-tag UX (closes DRIFT-648).
import { openBulkTagDialog } from "../../lib/bulk-tag-dialog";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<DonationStatus, string> = {
  pending: "Pending",
  succeeded: "Succeeded",
  refunded: "Refunded",
  failed: "Failed",
  charged_back: "Charged back",
  voided: "Voided",
  pending_review: "Pending review",
};
// CHG-1668 / ITER-3777 — provenance is intentionally omitted from this
// six-column list. Donation detail presents the canonical source axis.

function donorLabel(row: Donation): string {
  if (row.anonymous) return "Anonymous";
  if (row.donor_name) return row.donor_name;
  if (row.donor_email) return row.donor_email;
  return row.donor_id ? row.donor_id.slice(0, 8) + "…" : "—";
}

function designationLabel(row: Donation): string {
  return row.designation_name ?? row.designation_id.slice(0, 8) + "…";
}

// CHG-1668 / ITER-3777 — campaign column added per brief.
// row.campaign_name preferred; falls back to a truncated id when the
// donation has a campaign_id but the BE list-envelope didn't hydrate
// the name (cross-org join pattern matches designationLabel above).
function campaignLabel(row: Donation): string {
  if (!row.campaign_id) return "—";
  return row.campaign_name ?? row.campaign_id.slice(0, 8) + "…";
}

const donorIdColumn = (row: Donation): ReactNode => donorLabel(row);
const designationIdColumn = (row: Donation): ReactNode => designationLabel(row);
const campaignIdColumn = (row: Donation): ReactNode => campaignLabel(row);
// CHG-1668 / ITER-3777 — channelColumn dropped from the list view per
// the brief refocus on 6 columns. CHANNEL_LABEL is kept above because
// the detail-page status-cards still surface it (channelLabel resolver
// in donation-detail.ts).
const statusColumn = (row: Donation): ReactNode => STATUS_LABEL[row.status] ?? row.status;
// override-block:helpers END

export const donationListConfig: ListPageConfig<Donation> = {
  title: "Donations",
  subtitle: "Every gift recorded across all channels — search, audit, refund.",
  queryKey: ["donations", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search, filters }) =>
    donationsApi.listDonations({ page, pageSize, sortField, sortDirection, search, filters }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 50,
  rowHref: "/donations/{id}",
  search: { enabled: true, placeholder: "Search donations by donor name, email, or external ID…" },
  emptyState: {
    title: "No donations yet",
    subtitle: "Donations appear here as soon as your first gift is recorded — via widget, kiosk, donate page, or manual entry.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    { id: "export", label: "Export", variant: "ghost", permissionGate: toGate(GATE_CATALOG.donation_list["export"], "disable") },
    // override-block:header-actions END
  ],
  filters: [
    { field: "status", label: "Status", type: "select", options: [{ value: "succeeded", label: "Succeeded" }, { value: "refunded", label: "Refunded" }, { value: "failed", label: "Failed" }, { value: "disputed", label: "Disputed" }] },
    { field: "payment_method", label: "Payment method", type: "select", options: [{ value: "card", label: "Card" }, { value: "paypal", label: "PayPal" }, { value: "cash", label: "Cash" }, { value: "check", label: "Cheque" }, { value: "bank-transfer", label: "Bank transfer" }, { value: "bkash", label: "bKash" }, { value: "nagad", label: "Nagad" }, { value: "other", label: "Other" }] },
    { field: "date_range", label: "Date range", type: "date-range" },
    { field: "designation_id", label: "Designation", type: "entity-search", targetEntity: "designation" },
  ],
  bulkActions: [
    {
      id: "tag",
      label: "Tag selected",
      variant: "primary",
      permission: "alora-giving.tag.attach",
      permissionFallback: "hide",
      onAction:
        // override-block:bulk-action-tag BEGIN
        (ids) => openBulkTagDialog({ entityType: "donation", ids }),
        // override-block:bulk-action-tag END
    },
  ],
  columns: [
    { field: "created_at", label: "Recorded", type: "datetime", sortable: true },
    { field: "amount_minor_units", label: "Amount", type: "currency", currencyField: "currency", sortable: true },
    { field: "donor_id", label: "Donor", type: "custom", sortable: true, render: donorIdColumn },
    { field: "designation_id", label: "Designation", type: "custom", render: designationIdColumn },
    { field: "campaign_id", label: "Campaign", type: "custom", render: campaignIdColumn },
    { field: "status", label: "Status", type: "custom", sortable: true, render: statusColumn },
  ],
};
